import React from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Clock, Calendar, Shield } from "lucide-react";
import { getIntegrationConfig } from "../../config/integrationConfig";
import { Card, CardContent, CardHeader, CardFooter } from "../ui/card";

import { useToaster } from "../../contexts/ToasterContext";

const IntegrationCardGrid = ({
  integrations,
  isIntegrationProcessing,
  integrationType,
}) => {
  const integrationConfig = getIntegrationConfig(integrationType);
  const { showToast } = useToaster();
  const navigate = useNavigate();

  // Helper function to get nested value from object using dot notation
  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((current, key) => current?.[key], obj);
  };

  // Helper function to render field based on type
  const renderField = (field, integration) => {
    let value = getNestedValue(integration, field.key);
    let IconComponent = null;
    if (field.icon === "MapPin") IconComponent = MapPin;
    else if (field.icon === "Clock") IconComponent = Clock;
    else if (field.icon === "Calendar") IconComponent = Calendar;
    else if (field.icon === "Shield") IconComponent = Shield;

    switch (field.type) {
      case "title":
        return (
          <span className="relative group inline-flex items-center min-w-0">
            <h1
              className={`text-md font-semibold text-foreground ${
                field.truncate
                  ? "truncate max-w-[140px] sm:max-w-[180px] md:max-w-[220px] lg:max-w-[260px]"
                  : ""
              }`}
            >
              {value}
            </h1>
            {field.truncate && (
              <span className="pointer-events-none absolute left-0 sm:left-1/2 sm:-translate-x-1/2 top-full mt-2 z-20 hidden group-hover:block whitespace-normal break-words rounded-md bg-white border border-gray-200 shadow-lg text-[11px] text-gray-900 px-2 py-1 max-w-[80vw] sm:max-w-[320px]">
                {value}
              </span>
            )}
          </span>
        );

      case "subtitle":
        return (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              {IconComponent ? (
                <IconComponent className="h-3 w-3 text-muted-foreground" />
              ) : (
                <div className="h-2 w-2 rounded-full bg-gray-400"></div>
              )}
            </div>
            <span className="text-xs text-muted-foreground">
              {field.label}: {value || "Unknown"}
            </span>
          </div>
        );

      case "status":
        // Handle fallback logic: if value is 0 and fallbackKey is provided, use fallback value
        let finalValue = value;
        let useFallback = false;
        let zeroConfig = null;

        if (value === 0 && field.statusMap?.[0]?.fallbackKey) {
          const fallbackValue = getNestedValue(
            integration,
            field.statusMap[0].fallbackKey
          );
          if (fallbackValue !== undefined && fallbackValue !== null) {
            finalValue = fallbackValue;
            useFallback = true;
            zeroConfig = field.statusMap[0];
          }
        }

        // Get the appropriate config - use zeroConfig if fallback was used, otherwise normal lookup
        const statusConfig = useFallback
          ? zeroConfig
          : field.statusMap?.[finalValue] || field.statusMap?.default;
        let displayValue = finalValue;
        let colorClass = "bg-gray-400";

        if (statusConfig) {
          if (statusConfig.labelFormat?.includes("{fallbackValue}")) {
            displayValue = statusConfig.labelFormat.replace(
              "{fallbackValue}",
              finalValue
            );
          } else if (statusConfig.label?.includes("{value}")) {
            displayValue = statusConfig.label.replace("{value}", finalValue);
          } else {
            displayValue = statusConfig.label || finalValue;
          }

          switch (statusConfig.color) {
            case "green":
              colorClass = "bg-green-400";
              break;
            case "blue":
              colorClass = "bg-blue-400";
              break;
            case "orange":
              colorClass = "bg-orange-400";
              break;
            case "red":
              colorClass = "bg-red-400";
              break;
            case "gray":
              colorClass = "bg-gray-400";
              break;
            default:
              colorClass = "bg-gray-400";
          }
        }

        return (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <div className={`h-2 w-2 rounded-full ${colorClass}`}></div>
            </div>
            <span className="text-xs text-muted-foreground">
              {field.label}: {displayValue}
            </span>
          </div>
        );

      case "number":
        return (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <div className="h-2 w-2 rounded-full bg-gray-400"></div>
            </div>
            <span className="text-xs text-muted-foreground">
              {field.label}: {value || 0}
            </span>
          </div>
        );

      case "date":
        const formattedDate = value
          ? new Date(value).toLocaleDateString()
          : "Never";
        return (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <div className="h-2 w-2 rounded-full bg-gray-400"></div>
            </div>
            <span className="text-xs text-muted-foreground">
              {field.label}: {formattedDate}
            </span>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
      {integrations?.map((integration, idx) => {
        const hasNoFindings =
          !integration.totalFindings || integration.totalFindings === 0;
        const cardShouldBeClickable =
          integrationConfig?.isCardClickable !== false;
        const allowEmptyClick =
          integrationConfig?.features?.allowEmptyFindingsClick;
        const showCardFooter =
          integrationConfig?.showCardFooterBadges !== false;
        // Card is clickable if it should be clickable AND (has findings OR allows empty clicks)
        const isCardClickable = cardShouldBeClickable && (!hasNoFindings || allowEmptyClick);

        const handleCardClick = (e) => {
          if (hasNoFindings && !allowEmptyClick) {
            e.preventDefault();
            showToast("No findings available for this integration", "info");
            return;
          }

          // Use integrationType if provided, otherwise try to infer from integration data
          const type = integrationType || integration.type || "s3";

          // For storage integrations (s3, azure-blob, gcs), prioritize bucket_id
          const storageTypes = ["s3", "azure-blob", "gcs"];
          let id;

          // Function to check if a value is truly valid (not undefined, null, or empty string)
          const isValidId = (value) =>
            value !== undefined && value !== null && value !== "";

          if (storageTypes.includes(type)) {
            // For storage, try bucket_id first, then fallback to others
            if (isValidId(integration.bucket_id)) {
              id = integration.bucket_id;
            } else if (isValidId(integration.integration_id)) {
              id = integration.integration_id;
            } else if (isValidId(integration.id)) {
              id = integration.id;
            } else if (isValidId(integration.repositoryId)) {
              id = integration.repositoryId;
            }
          } else {
            // For non-storage, try integration_id first
            if (isValidId(integration.integration_id)) {
              id = integration.integration_id;
            } else if (isValidId(integration.id)) {
              id = integration.id;
            } else if (isValidId(integration.repositoryId)) {
              id = integration.repositoryId;
            } else if (isValidId(integration.repository_id)) {
              // Some backends return snake_case repository_id
              id = integration.repository_id;
            } else if (isValidId(integration.bucket_id)) {
              id = integration.bucket_id;
            }
          }

          // Validate that we have a valid ID before proceeding
          if (!isValidId(id)) {
            console.error("No valid ID found for integration:", integration);
            showToast("Unable to navigate: Missing integration ID", "error");
            return;
          }

          const name = integration.name || integration.title;

          // Clean up any corrupted sessionStorage entries with "undefined" keys
          try {
            const corruptedKeys = [];
            for (let i = 0; i < sessionStorage.length; i++) {
              const key = sessionStorage.key(i);
              if (key && key.includes("_undefined")) {
                corruptedKeys.push(key);
              }
            }
            corruptedKeys.forEach((key) => {
              sessionStorage.removeItem(key);
            });
          } catch (cleanupErr) {
            // Silently handle cleanup errors
          }

          // Cache the integration name in sessionStorage for immediate display
          if (name && type && id) {
            const cacheKey = `integration_name_${type}_${id}`;
            const cacheData = {
              name: name,
              timestamp: Date.now(),
            };
            sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
          }

          // Special routing for Cloud IAM providers
          if (["aws", "azure", "gcp"].includes(type)) {
            navigate(`/identity-management/iam/${type}/${id}`);
          } else {
            // All other integrations (github, s3, active-directory, etc.) use IntegrationListPage pattern
            navigate(`/integration/${type}/${id}`);
          }
        };

        const getIntegrationIcon = () => {
          return integrationConfig?.icon || "/integration.svg";
        };

        const IntegrationCard = () => (
          <Card
            className={`h-full hover:shadow-lg transition-all duration-200 bg-[#F9F9F9] flex flex-col rounded-xl ${
              isCardClickable
                ? "cursor-pointer"
                : "cursor-not-allowed opacity-75"
            }`}
            onClick={
              isCardClickable
                ? handleCardClick
                : (e) => e.preventDefault()
            }
          >
            <CardHeader className="py-2 px-3 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <img
                    src={getIntegrationIcon(integration.type)}
                    alt={integration.type || "Integration"}
                    className="h-4 w-4 flex-shrink-0"
                  />
                  <span className="relative group inline-flex items-center min-w-0">
                    <h1 className="text-md font-semibold text-foreground truncate max-w-[140px] sm:max-w-[180px] md:max-w-[220px] lg:max-w-[260px]">
                      {integration.name || integration.title}
                    </h1>
                    <span className="pointer-events-none absolute left-0 sm:left-1/2 sm:-translate-x-1/2 top-full mt-2 z-20 hidden group-hover:block whitespace-normal break-words rounded-md bg-white border border-gray-200 shadow-lg text-[11px] text-gray-900 px-2 py-1 max-w-[80vw] sm:max-w-[320px]">
                      {integration.name || integration.title}
                    </span>
                  </span>
                </div>
              </div>
            </CardHeader>

            <CardContent className="mt-2 pb-2 px-3 flex-1 flex items-center">
              <div className="space-y-3">
                {integrationConfig?.cardFields
                  ?.filter((field) => field.type !== "title")
                  .map((field, index) => (
                    <div key={index}>{renderField(field, integration)}</div>
                  )) || (
                  // Fallback to original layout if no config
                  <>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <div className="h-2 w-2 rounded-full bg-gray-400"></div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        Type: {integration.type || "Unknown"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <div
                          className={`h-2 w-2 rounded-full ${
                            integration.status === "active"
                              ? "bg-green-400"
                              : integration.status === "scanning"
                              ? "bg-orange-400"
                              : "bg-gray-400"
                          }`}
                        ></div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        Status:{" "}
                        {integration.status === "active"
                          ? "Critical"
                          : integration.status === "scanning"
                          ? "Scanning"
                          : "Inactive"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <div className="h-2 w-2 rounded-full bg-gray-400"></div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        Last Scan:{" "}
                        {integration.lastScan
                          ? new Date(integration.lastScan).toLocaleDateString()
                          : "Never"}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </CardContent>

            {showCardFooter && (
              <CardFooter className="px-3 py-2">
                <div className="flex items-center justify-between gap-2 w-full flex-wrap">
                  {/* Custom Footer for Active Directory/Risk & Predictability */}
                  {integration.risk_level || integration.predictability ? (
                    <>
                      {integration.predictability && (
                        <div className="bg-gray-100 px-3 py-1 rounded text-center flex items-center justify-center min-h-[28px]">
                          <span className="text-xs font-medium text-gray-700">
                            {integration.predictability} Predictability
                          </span>
                        </div>
                      )}

                      {integration.risk_level && (
                        <div
                          className={`px-3 py-1 rounded text-center flex items-center justify-center min-h-[28px] ${
                            integration.risk_level.toLowerCase() === "high"
                              ? "bg-red-100 text-red-800"
                              : integration.risk_level.toLowerCase() ===
                                "medium"
                              ? "bg-orange-100 text-orange-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          <span className="text-xs font-medium">
                            {integration.risk_level}
                          </span>
                        </div>
                      )}
                    </>
                  ) : integrationConfig?.footer ? (
                    // Detailed Footer for IAM Scans etc
                    <>
                      {integration.critical > 0 && (
                        <div className="bg-red-100 text-red-800 px-2 py-1 rounded text-center flex items-center justify-center min-h-[24px]">
                          <span className="text-[10px] sm:text-xs font-medium">
                            Critical {integration.critical}
                          </span>
                        </div>
                      )}
                      {integration.high > 0 && (
                        <div className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-center flex items-center justify-center min-h-[24px]">
                          <span className="text-[10px] sm:text-xs font-medium">
                            High {integration.high}
                          </span>
                        </div>
                      )}
                      {integration.medium > 0 && (
                        <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-center flex items-center justify-center min-h-[24px]">
                          <span className="text-[10px] sm:text-xs font-medium">
                            Medium {integration.medium}
                          </span>
                        </div>
                      )}
                      {integration.low > 0 && (
                        <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-center flex items-center justify-center min-h-[24px]">
                          <span className="text-[10px] sm:text-xs font-medium">
                            Low {integration.low}
                          </span>
                        </div>
                      )}
                      {(!integration.totalFindings ||
                        integration.totalFindings === 0) && (
                        <div className="bg-green-100 border border-green-200 px-3 py-1 rounded text-center flex items-center justify-center min-h-[28px] w-full">
                          <span className="text-xs font-medium text-green-700">
                            Secure
                          </span>
                        </div>
                      )}
                      {integration.totalFindings > 0 &&
                        !integration.critical &&
                        !integration.high &&
                        !integration.medium &&
                        !integration.low && (
                          // Fallback if detailed counts are missing but total > 0
                          <div className="bg-gray-100 px-3 py-1 rounded text-center flex items-center justify-center min-h-[28px]">
                            <span className="text-xs font-medium text-foreground">
                              Findings {integration.totalFindings}
                            </span>
                          </div>
                        )}
                    </>
                  ) : (
                    // Standard Footer
                    <>
                      <div className="bg-gray-100 px-3 py-1 rounded text-center flex items-center justify-center min-h-[28px]">
                        <span className="text-xs font-medium text-foreground">
                          Findings {integration.totalFindings || 0}
                        </span>
                      </div>

                      {integration.verifiedFindings > 0 ? (
                        <div className="bg-red-100 text-red-800 px-3 py-1 rounded text-center flex items-center justify-center min-h-[28px]">
                          <span className="text-xs font-medium">
                            Critical {integration.verifiedFindings}
                          </span>
                        </div>
                      ) : (integration.inactiveFindings || 0) > 0 ? (
                        <div className="bg-yellow-100 border border-yellow-200 px-3 py-1 rounded text-center flex items-center justify-center min-h-[28px]">
                          <span className="text-xs font-medium text-yellow-700">
                            Risky {integration.inactiveFindings}
                          </span>
                        </div>
                      ) : (integration.totalFindings || 0) > 0 ? (
                        <div className="bg-yellow-100 border border-yellow-200 px-3 py-1 rounded text-center flex items-center justify-center min-h-[28px]">
                          <span className="text-xs font-medium text-yellow-700">
                            Risky
                          </span>
                        </div>
                      ) : (
                        <div className="bg-green-100 border border-green-200 px-3 py-1 rounded text-center flex items-center justify-center min-h-[28px]">
                          <span className="text-xs font-medium text-green-700">
                            Secure
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardFooter>
            )}
          </Card>
        );

        return (
          <div
            key={(() => {
              const type = integrationType || integration.type || "s3";
              const storageTypes = ["s3", "azure-blob", "gcs"];
              const isValidId = (value) =>
                value !== undefined && value !== null && value !== "";

              let keyId;
              if (storageTypes.includes(type)) {
                if (isValidId(integration.bucket_id))
                  keyId = integration.bucket_id;
                else if (isValidId(integration.integration_id))
                  keyId = integration.integration_id;
                else if (isValidId(integration.id)) keyId = integration.id;
                else if (isValidId(integration.repository_id))
                  keyId = integration.repository_id;
                else keyId = idx;
              } else {
                if (isValidId(integration.integration_id))
                  keyId = integration.integration_id;
                else if (isValidId(integration.id)) keyId = integration.id;
                else if (isValidId(integration.repositoryId))
                  keyId = integration.repositoryId;
                else if (isValidId(integration.bucket_id))
                  keyId = integration.bucket_id;
                else keyId = idx;
              }

              return keyId;
            })()}
          >
            <IntegrationCard />
          </div>
        );
      })}
    </div>
  );
};

export default IntegrationCardGrid;
